import { Box, Button, Link as MuiLink, Stack, TextField } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { API_BASE_URL, authFetchJson } from '../../apiClient.ts';
import { formatReportDate } from '../../utils/helperUtils.ts';

interface IntroductionRow {
  id: number;
  dateIntroduced: string;
  fromContactId: number; // introducer (e.g. Michael)
  fromContactName: string;
  toContactId: number; // person introduced to CRM user (e.g. David)
  toContactName: string;
}

interface Props {
  isDashboard?: boolean;
}

export const IntroductionsReport: React.FC<Props> = ({ isDashboard = false }) => {
  const [rows, setRows] = useState<IntroductionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    if (isDashboard) {
      authFetchJson(`${API_BASE_URL}/dashboard/introductions`)
        .then(({ json }) => {
          setRows(json);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error loading dashboard data:', error);
          setLoading(false);
        });
    } else {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const qs = params.toString();
      const url = `${API_BASE_URL}/reports/introductions${qs ? `?${qs}` : ''}`;
      setLoading(true);
      authFetchJson(url)
        .then(({ json }) => {
          setRows(json);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load introductions report', err);
          setLoading(false);
        });
    }
  }, [isDashboard, startDate, endDate]);

  const exportToCSV = () => {
    const headers = ['Date', 'Introduced By', 'Introduced To'];
    const csvRows = rows.map((r) => [
      formatReportDate(r.dateIntroduced),
      r.fromContactName,
      r.toContactName
    ]);

    const content = [headers, ...csvRows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `introductions-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: GridColDef[] = [
    {
      field: 'toContactName',
      headerName: 'Introduced To',
      width: 250,
      renderCell: (params) => (
        <MuiLink
          component={Link}
          to={`/contacts/${params.row.toContactId}/show`}
          target="_blank"
          underline="hover"
        >
          {params.value}
        </MuiLink>
      )
    },
    {
      field: 'fromContactName',
      headerName: 'Introduced By',
      width: 250,
      renderCell: (params) => (
        <MuiLink
          component={Link}
          to={`/contacts/${params.row.fromContactId}/show`}
          target="_blank"
          underline="hover"
        >
          {params.value}
        </MuiLink>
      )
    },
    {
      field: 'dateIntroduced',
      headerName: 'Introduced On',
      width: 250,
      renderCell: (params) => formatReportDate(params.value)
    }
  ];

  const hasDateFilter = Boolean(startDate || endDate);

  return (
    <Box>
      {!isDashboard && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          mb={2}
          sx={{ flexWrap: 'wrap', rowGap: 1 }}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 160 }}
          />
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => {
              setStartDate('');
              setEndDate('');
            }}
            disabled={!hasDateFilter}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Clear
          </Button>
          <Box flexGrow={1} />
          <Button
            size="small"
            variant="outlined"
            onClick={exportToCSV}
            startIcon={<DownloadIcon fontSize="small" />}
            disabled={rows.length === 0}
          >
            Download CSV
          </Button>
        </Stack>
      )}
      <Box sx={{ height: isDashboard ? 320 : 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: isDashboard ? 10 : 25, page: 0 } },
            sorting: {
              sortModel: [{ field: 'dateIntroduced', sort: 'desc' }]
            }
          }}
          density={isDashboard ? 'compact' : 'standard'}
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};
